/**
 * Card inline da alteração proposta pelo Agente (E23.F8 — ADR-0025). // ux-neutrality:allow doc-comment
 *
 * Mostra antigo→novo + o resumo da alteração, com Aplicar/Descartar. A // ux-neutrality:allow doc-comment
 * gravação só acontece quando o perito clica em Aplicar — o Agente nunca // ux-neutrality:allow doc-comment
 * escreve sozinho (defensibilidade forense). // ux-neutrality:allow doc-comment
 *
 * E54.F8.T05: objeto de blueprint com kicker. O contorno é em aço porque a
 * proposta é **direção** (algo a decidir), não resultado — a rampa de sinal
 * fica reservada ao que decide.
 */
import { Blueprint, Button, CardKicker } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { narrativeFieldLabel } from '@/lib/laudo_fields';
import type { LaudoEditProposalData } from '@/lib/laudo_edits';

interface LaudoEditProposalCardProps {
  proposal: LaudoEditProposalData;
  currentValue: string;
  applying: boolean;
  errorMessage: string | null;
  onApprove: () => void;
  onDiscard: () => void;
}

export function LaudoEditProposalCard({
  proposal,
  currentValue,
  applying,
  errorMessage,
  onApprove,
  onDiscard,
}: LaudoEditProposalCardProps): JSX.Element {
  const t = ptBR.laudoCaso.editProposal;
  return (
    <Blueprint
      className="mt-sc-3 border-accent p-sc-3"
      data-testid="laudo-edit-proposal"
      role="group"
      aria-label={t.title}
    >
      <CardKicker className="block">{t.title}</CardKicker>
      <p className="mt-1 text-caption text-fg-muted">
        {t.fieldLabel.replace('{{field}}', narrativeFieldLabel(proposal.target_field))}
      </p>
      <div className="mt-sc-2 grid gap-sc-2 sm:grid-cols-2">
        <div>
          <p className="text-caption font-medium text-fg-muted">{t.currentLabel}</p>
          <p
            className="mt-0.5 whitespace-pre-wrap text-body-sm text-fg-muted line-through"
            data-testid="laudo-edit-proposal-current"
          >
            {currentValue}
          </p>
        </div>
        <div>
          <p className="text-caption font-medium text-fg">{t.proposedLabel}</p>
          <p
            className="mt-0.5 whitespace-pre-wrap text-body-sm text-fg"
            data-testid="laudo-edit-proposal-proposed"
          >
            {proposal.proposed_value}
          </p>
        </div>
      </div>
      <p className="mt-sc-2 text-caption text-fg-muted">
        <span className="font-medium">{t.rationaleLabel}: </span>
        {proposal.rationale}
      </p>
      {errorMessage && (
        <p role="alert" className="mt-sc-2 text-caption text-danger-700">
          {errorMessage}
        </p>
      )}
      <div className="mt-sc-3 flex gap-sc-2">
        <Button
          type="button"
          size="sm"
          onClick={onApprove}
          disabled={applying}
          data-testid="laudo-edit-proposal-approve"
        >
          {applying ? t.applying : t.approveCta}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onDiscard}
          disabled={applying}
          data-testid="laudo-edit-proposal-discard"
        >
          {t.discardCta}
        </Button>
      </div>
    </Blueprint>
  );
}
